/**
 * AIMetadataDetails — Affichage exhaustif des métadonnées IA d'un produit
 *
 * Composant unique utilisé par ProductDetailModal (catalogue) ET
 * WardrobeItemDetailModal (dressing) pour garantir une expérience visuelle
 * cohérente. Un seul endroit à maintenir.
 *
 * Affiche TOUS les champs renseignés par Claude Vision (catalog-analysis-cron) :
 *   - subcategory, primaryColor, colorDetail, secondaryColors, colorTone, colorFamily
 *   - material, pattern, fit, silhouette, length, neckline, sleeveLength
 *   - formality (avec barre + label) + versatility
 *   - styleScores (top 4 ≥ 50, avec barres)
 *   - occasions (top 4 ≥ 50, en chips)
 *   - seasonality + temperatureRange + timeOfDay
 *   - styleTags
 *   - isTrendy/trendScore/trendTag/trendReason
 *   - priceRange, nameFr (si renseigné)
 *
 * Si pas d'ai_metadata → renvoie null (placeholder à gérer côté parent).
 *
 * Props :
 *   - aiMetadata : objet ai_metadata du produit
 *   - compact?  : si true, masque la palette de couleurs détaillée et les
 *                 champs très techniques. Défaut false.
 *   - showHeader? : si true, affiche un header "🤖 ANALYSE IA". Défaut true.
 *   - currentTempC? : température actuelle en °C. Si fournie, la plage
 *                     température est comparée et un warning visuel apparaît
 *                     si l'item est inadapté à la météo du jour.
 */
import React from 'react';
import { View, Text } from 'react-native';
import { useI18n } from '../i18n';
import { translateField, translateArray } from '../utils/translateAIMetadata';

// ─── Helpers ──────────────────────────────────────────────────────────────
const formalityLabel = (f, en = false) => {
  if (typeof f !== 'number') return null;
  if (f <= 2) return { label: en ? 'Very casual'  : 'Très casual',  icon: '🩴', color: '#558B2F' };
  if (f <= 4) return { label: en ? 'Casual'       : 'Casual',       icon: '👕', color: '#7CB342' };
  if (f <= 6) return { label: en ? 'Smart casual' : 'Smart casual', icon: '👔', color: '#1976D2' };
  if (f <= 8) return { label: en ? 'Formal'       : 'Formel',       icon: '🧥', color: '#5E35B1' };
  return        { label: en ? 'Very formal'      : 'Très formel',  icon: '🎩', color: '#311B92' };
};

const seasonLabels = (en = false) => ({
  printemps: en ? '🌸 Spring' : '🌸 Printemps',
  été:       en ? '☀️ Summer' : '☀️ Été',
  automne:   en ? '🍂 Autumn' : '🍂 Automne',
  hiver:     en ? '❄️ Winter' : '❄️ Hiver',
  spring:    en ? '🌸 Spring' : '🌸 Printemps',
  summer:    en ? '☀️ Summer' : '☀️ Été',
  autumn:    en ? '🍂 Autumn' : '🍂 Automne',
  fall:      en ? '🍂 Autumn' : '🍂 Automne',
  winter:    en ? '❄️ Winter' : '❄️ Hiver',
});

const timeOfDayLabels = (en = false) => ({
  jour:          en ? '☀️ Daytime' : '☀️ Jour',
  soir:          en ? '🌙 Evening' : '🌙 Soir',
  'toute-heure': en ? '🕐 Anytime' : '🕐 Toute heure',
});

const priceRangeLabels = (en = false) => ({
  budget:      { label: en ? 'Budget'    : 'Budget',   icon: '💰' },
  'mid-range': { label: en ? 'Mid-range' : 'Mi-gamme', icon: '💵' },
  premium:     { label: 'Premium',                     icon: '💎' },
  luxury:      { label: en ? 'Luxury'    : 'Luxe',     icon: '👑' },
});

const versatilityComment = (v, en = false) => {
  if (v >= 8) return en ? 'essential piece'  : 'pièce essentielle';
  if (v >= 6) return en ? 'many occasions'   : "beaucoup d'occasions";
  if (v >= 4) return en ? 'varied uses'      : 'usages variés';
  return        en ? 'specific use'        : 'usage spécifique';
};

const colorToneEmoji = (tone) => {
  if (tone === 'chaud' || tone === 'warm') return '🔥';
  if (tone === 'froid' || tone === 'cold' || tone === 'cool') return '❄️';
  if (tone === 'neutre' || tone === 'neutral') return '🌫️';
  return '';
};

const colorToneLabel = (tone, en = false) => {
  if (!tone) return '';
  if (!en) return tone;
  const map = {
    chaud: 'warm',
    froid: 'cold',
    neutre: 'neutral',
    warm: 'warm',
    cold: 'cold',
    cool: 'cool',
    neutral: 'neutral',
  };
  return map[tone.toLowerCase?.()] || tone;
};

// ─── Translation maps for backend FR values ─────────────────────────────
const STYLE_LABEL_MAP = {
  chic: 'Chic',
  casual: 'Casual',
  minimaliste: 'Minimal',
  minimalist: 'Minimal',
  bohème: 'Boho',
  boheme: 'Boho',
  bohemian: 'Boho',
  romantique: 'Romantic',
  romantic: 'Romantic',
  sportif: 'Sport',
  sport: 'Sport',
  sporty: 'Sport',
  streetwear: 'Streetwear',
  street: 'Streetwear',
  élégant: 'Elegant',
  elegant: 'Elegant',
  vintage: 'Vintage',
  edgy: 'Edgy',
  workwear: 'Workwear',
  preppy: 'Preppy',
};

const OCCASION_LABEL_MAP = {
  bureau: 'Office',
  office: 'Office',
  work: 'Office',
  soirée: 'Evening',
  soiree: 'Evening',
  evening: 'Evening',
  mariage: 'Wedding',
  wedding: 'Wedding',
  sport: 'Sport',
  casual: 'Casual',
  vacances: 'Vacation',
  vacation: 'Vacation',
  holiday: 'Vacation',
  cérémonie: 'Ceremony',
  ceremonie: 'Ceremony',
  ceremony: 'Ceremony',
  quotidien: 'Everyday',
  everyday: 'Everyday',
  weekend: 'Weekend',
  date: 'Date',
};

const MATERIAL_LABEL_MAP = {
  coton: 'Cotton',
  cotton: 'Cotton',
  laine: 'Wool',
  wool: 'Wool',
  soie: 'Silk',
  silk: 'Silk',
  lin: 'Linen',
  linen: 'Linen',
  polyester: 'Polyester',
  cuir: 'Leather',
  leather: 'Leather',
  jean: 'Denim',
  denim: 'Denim',
  cachemire: 'Cashmere',
  cashmere: 'Cashmere',
  synthétique: 'Synthetic',
  synthetique: 'Synthetic',
  synthetic: 'Synthetic',
  viscose: 'Viscose',
};

const COLOR_LABEL_MAP = {
  noir: 'Black',
  black: 'Black',
  blanc: 'White',
  white: 'White',
  gris: 'Gray',
  gray: 'Gray',
  grey: 'Gray',
  rouge: 'Red',
  red: 'Red',
  bleu: 'Blue',
  blue: 'Blue',
  vert: 'Green',
  green: 'Green',
  jaune: 'Yellow',
  yellow: 'Yellow',
  orange: 'Orange',
  violet: 'Purple',
  purple: 'Purple',
  rose: 'Pink',
  pink: 'Pink',
  marron: 'Brown',
  brown: 'Brown',
  doré: 'Gold',
  dore: 'Gold',
  gold: 'Gold',
  beige: 'Beige',
  écru: 'Ecru',
  ecru: 'Ecru',
  bordeaux: 'Burgundy',
  burgundy: 'Burgundy',
  kaki: 'Khaki',
  khaki: 'Khaki',
  marine: 'Navy',
  navy: 'Navy',
  camel: 'Camel',
  crème: 'Cream',
  creme: 'Cream',
  cream: 'Cream',
  taupe: 'Taupe',
  ivoire: 'Ivory',
  ivory: 'Ivory',
  // v1.12.528 — variantes composées + neutres additionnels
  argent: 'Silver', silver: 'Silver',
  bronze: 'Bronze',
  cuivre: 'Copper', copper: 'Copper',
  anthracite: 'Charcoal', charcoal: 'Charcoal',
  sable: 'Sand', sand: 'Sand',
  'bleu marine': 'Navy', 'bleu nuit': 'Midnight Blue',
  'bleu ciel': 'Sky Blue', 'bleu pastel': 'Pastel Blue', 'bleu clair': 'Light Blue',
  'rose pâle': 'Pale Pink', 'rose pale': 'Pale Pink', 'rose poudré': 'Powder Pink',
  'rose fuchsia': 'Fuchsia', fuchsia: 'Fuchsia',
  'vert kaki': 'Khaki Green', 'vert olive': 'Olive Green', olive: 'Olive',
  'vert d\'eau': 'Aqua Green', menthe: 'Mint', mint: 'Mint',
  'gris clair': 'Light Gray', 'gris foncé': 'Dark Gray',
  'rouge bordeaux': 'Burgundy', 'rouge vif': 'Bright Red',
  'blanc cassé': 'Off-White', 'blanc casse': 'Off-White',
  multicolore: 'Multicolor', multicolor: 'Multicolor',
  pourpre: 'Crimson', crimson: 'Crimson',
  corail: 'Coral', coral: 'Coral',
  saumon: 'Salmon', salmon: 'Salmon',
  pêche: 'Peach', peach: 'Peach',
  lavande: 'Lavender', lavender: 'Lavender',
  turquoise: 'Turquoise',
  mauve: 'Mauve',
  moutarde: 'Mustard', mustard: 'Mustard',
  chocolat: 'Chocolate', chocolate: 'Chocolate',
  café: 'Coffee', cafe: 'Coffee',
};

// v1.12.528 — Dictionnaire FR → EN pour types/noms de vêtements courants
// (fallback quand Claude n'a pas rempli nameEn/detectedTypeEn)
const TYPE_FR_EN = {
  // Catégories pluriels (depuis ai_metadata.category ou subcategory)
  'hauts': 'Tops', 'bas': 'Bottoms', 'robes': 'Dresses', 'vestes': 'Jackets',
  'manteaux': 'Coats', 'chaussures': 'Shoes', 'sacs': 'Bags',
  'bijoux': 'Jewelry', 'accessoires': 'Accessories', 'maillots': 'Swimwear',
  // Hauts
  'pull': 'Sweater', 'pull-over': 'Pullover', 'sweat': 'Sweatshirt', 'hoodie': 'Hoodie',
  'gilet': 'Cardigan', 'cardigan': 'Cardigan',
  't-shirt': 'T-Shirt', 'tee-shirt': 'T-Shirt', 'tshirt': 'T-Shirt',
  'chemise': 'Shirt', 'chemisier': 'Blouse', 'blouse': 'Blouse',
  'top': 'Top', 'débardeur': 'Tank Top', 'débardeurs': 'Tank Top',
  'polo': 'Polo', 'tunique': 'Tunic',
  // Bas
  'jean': 'Jeans', 'pantalon': 'Pants', 'pantalons': 'Pants',
  'short': 'Shorts', 'shorts': 'Shorts', 'bermuda': 'Bermuda',
  'jupe': 'Skirt', 'legging': 'Leggings', 'jogging': 'Joggers',
  // Robes
  'robe': 'Dress', 'combinaison': 'Jumpsuit', 'salopette': 'Overalls',
  // Outerwear
  'veste': 'Jacket', 'blazer': 'Blazer', 'manteau': 'Coat',
  'trench': 'Trench Coat', 'parka': 'Parka', 'doudoune': 'Puffer Jacket',
  'bomber': 'Bomber Jacket', 'perfecto': 'Biker Jacket',
  // Chaussures
  'baskets': 'Sneakers', 'sneakers': 'Sneakers', 'tennis': 'Sneakers',
  'bottes': 'Boots', 'bottines': 'Ankle Boots',
  'escarpins': 'Heels', 'talons': 'Heels',
  'sandales': 'Sandals', 'mules': 'Mules', 'mocassins': 'Loafers',
  'ballerines': 'Flats', 'derbies': 'Oxfords',
  // Accessoires
  'sac': 'Bag', 'sac à main': 'Handbag', 'sac à dos': 'Backpack',
  'pochette': 'Clutch', 'cabas': 'Tote Bag',
  'ceinture': 'Belt', 'écharpe': 'Scarf', 'foulard': 'Scarf',
  'chapeau': 'Hat', 'casquette': 'Cap', 'lunettes': 'Glasses',
  // Bijoux
  'collier': 'Necklace', 'bracelet': 'Bracelet', 'bague': 'Ring',
  'boucles d\'oreille': 'Earrings', 'boucles d\'oreilles': 'Earrings',
  'montre': 'Watch',
  // Modificateurs (couleurs/matières dans noms composés)
  'rouge': 'red', 'noir': 'black', 'blanc': 'white', 'bleu': 'blue',
  'vert': 'green', 'jaune': 'yellow', 'rose': 'pink', 'orange': 'orange',
  'gris': 'gray', 'beige': 'beige', 'marron': 'brown', 'violet': 'purple',
  'argent': 'silver', 'doré': 'gold', 'or': 'gold',
  'en laine': 'wool', 'en coton': 'cotton', 'en lin': 'linen', 'en soie': 'silk',
  'en cuir': 'leather', 'en daim': 'suede', 'en jean': 'denim', 'en denim': 'denim',
  'à fleurs': 'floral', 'à pois': 'polka dot', 'à rayures': 'striped',
  'imprimé': 'printed', 'imprimée': 'printed', 'uni': 'plain', 'unie': 'plain',
  'court': 'short', 'courte': 'short', 'long': 'long', 'longue': 'long',
  'midi': 'midi', 'maxi': 'maxi', 'mini': 'mini',
  'slim': 'slim', 'large': 'wide', 'oversized': 'oversized',
};

function translateTypeNameToEn(text) {
  if (!text || typeof text !== 'string') return text;
  // Cherche d'abord match exact (lowercase)
  const lower = text.toLowerCase().trim();
  if (TYPE_FR_EN[lower]) return TYPE_FR_EN[lower];
  // Sinon traduit mot par mot
  let translated = text;
  // Trier les clés par longueur décroissante pour matcher d'abord les expressions longues
  const keys = Object.keys(TYPE_FR_EN).sort((a, b) => b.length - a.length);
  for (const fr of keys) {
    const en = TYPE_FR_EN[fr];
    // Remplacement insensible à la casse, frontière de mot
    const re = new RegExp(`\\b${fr.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'gi');
    translated = translated.replace(re, en);
  }
  // Capitaliser première lettre
  return translated.charAt(0).toUpperCase() + translated.slice(1);
}

const translateFromMap = (value, map, en) => {
  if (!value || typeof value !== 'string') return value;
  if (!en) return value;
  const key = value.toLowerCase().trim();
  return map[key] || value;
};

const translateStyleTag = (tag, en) => translateFromMap(tag, STYLE_LABEL_MAP, en);
const translateOccasion = (occ, en) => translateFromMap(occ, OCCASION_LABEL_MAP, en);
const translateColor    = (c, en)   => translateFromMap(c, COLOR_LABEL_MAP, en);
const translateMaterial = (m, en)   => translateFromMap(m, MATERIAL_LABEL_MAP, en);

// v1.12.529 — Export pour usage externe (WardrobeScreen grid, etc.)
export { translateColor, translateTypeNameToEn };

// Translate a color label that may contain a "primary (detail)" pattern
const translateColorWithDetail = (raw, en) => {
  if (!raw || !en) return raw;
  // Match "primary (detail)" pattern
  const m = raw.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (m) {
    return `${translateColor(m[1].trim(), en)} (${translateColor(m[2].trim(), en)})`;
  }
  return translateColor(raw, en);
};

// ─── Composant ────────────────────────────────────────────────────────────
export default function AIMetadataDetails({ aiMetadata, compact = false, showHeader = true, currentTempC = null }) {
  const { lang } = useI18n();
  const en = lang === 'en';

  const ai = aiMetadata;
  if (!ai || typeof ai !== 'object') return null;

  // Vérifier qu'on a au moins des données utiles
  const hasData = ai.subcategory
    || ai.detectedType
    || ai.styleTags?.length > 0
    || ai.material
    || ai.pattern
    || ai.fit
    || typeof ai.formality === 'number'
    || ai.styleScores
    || ai.occasions
    || ai.isTrendy
    || ai.seasonality?.length > 0
    || ai.primaryColor;
  if (!hasData) return null;

  // Top 4 styles (au lieu de 3 — on a la place)
  const topStyles = ai.styleScores
    ? Object.entries(ai.styleScores)
        .filter(([, v]) => typeof v === 'number' && v >= 50)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
    : [];

  // Top 4 occasions
  const topOccasions = ai.occasions
    ? Object.entries(ai.occasions)
        .filter(([, v]) => typeof v === 'number' && v >= 50)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
    : [];

  const fmt = formalityLabel(ai.formality, en);
  const seasons = seasonLabels(en);
  const timesOfDay = timeOfDayLabels(en);
  const prices = priceRangeLabels(en);

  // Couleurs : enrichir affichage si on a colorDetail
  const colorParts = [];
  if (ai.primaryColor) {
    if (ai.colorDetail && ai.colorDetail !== ai.primaryColor) {
      colorParts.push(`${translateColor(ai.primaryColor, en)} (${translateColor(ai.colorDetail, en)})`);
    } else {
      colorParts.push(translateColor(ai.primaryColor, en));
    }
  }
  if (Array.isArray(ai.secondaryColors) && ai.secondaryColors.length > 0) {
    colorParts.push(...ai.secondaryColors.slice(0, 3).map(c => translateColor(c, en)));
  }

  return (
    <View style={{
      marginTop: 16, marginBottom: 4,
      padding: 14,
      backgroundColor: '#FAF8F3',
      borderRadius: 12,
      borderWidth: 1,
      borderColor: 'rgba(212, 175, 55, 0.25)',
    }}>
      {/* Header avec badge IA + tendance */}
      {showHeader && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: 13 }}>🤖</Text>
            <Text style={{ fontSize: 10, letterSpacing: 2.5, fontWeight: '700', color: '#8B6F2E' }}>
              {en ? 'AI ANALYSIS' : 'ANALYSE IA'}
            </Text>
          </View>
          {(ai.isTrendy || (ai.trendScore || 0) >= 70) && (
            <View style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8, backgroundColor: '#FF4757' }}>
              <Text style={{ fontSize: 9, color: '#fff', fontWeight: '700', letterSpacing: 0.5 }}>
                {en ? 'TRENDING' : 'TENDANCE'}{ai.trendScore ? ` ${ai.trendScore}` : ''}
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Type détecté : v1.12.331/528 — utilise nameEn/detectedTypeEn si dispo,
          sinon traduit FR→EN runtime via translateTypeNameToEn (pour anciens items) */}
      {(ai.subcategory || ai.detectedType) && (() => {
        const frName = ai.subcategory || ai.detectedType;
        const displayName = en
          ? (ai.nameEn || ai.detectedTypeEn || translateTypeNameToEn(frName))
          : frName;
        return (
          <View style={{ marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.05)' }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>{en ? 'Detected type' : 'Type détecté'}</Text>
            <Text style={{ fontSize: 14, color: '#1A1A1A', fontWeight: '600', textTransform: 'capitalize' }}>
              🏷️ {displayName}
            </Text>
            {!en && ai.nameFr && ai.nameFr !== frName && (
              <Text style={{ fontSize: 11, color: '#888', marginTop: 2 }}>↳ {ai.nameFr}</Text>
            )}
          </View>
        );
      })()}

      {/* Tags de style (en haut, c'est la signature stylistique) */}
      {Array.isArray(ai.styleTags) && ai.styleTags.length > 0 && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {ai.styleTags.slice(0, 6).map((tag, idx) => (
            <View key={`${tag}-${idx}`} style={{
              paddingHorizontal: 9, paddingVertical: 4,
              backgroundColor: '#0A0A0A', borderRadius: 12,
            }}>
              <Text style={{ fontSize: 10, color: '#fff', fontWeight: '600', letterSpacing: 0.3 }}>
                {translateStyleTag(tag, en)}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Couleurs (palette détaillée) */}
      {colorParts.length > 0 && !compact && (
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            {en ? 'Colors' : 'Couleurs'}
          </Text>
          <Text style={{ fontSize: 12, color: '#222', fontWeight: '500' }}>
            🎨 {colorParts.join(' · ')}
            {ai.colorTone && (
              <Text style={{ color: '#888' }}>  {colorToneEmoji(ai.colorTone)} {en ? `${colorToneLabel(ai.colorTone, en)} tone` : `ton ${ai.colorTone}`}</Text>
            )}
          </Text>
        </View>
      )}

      {/* Caractéristiques en grille 2 colonnes */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        {ai.material && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>{en ? 'Material' : 'Matière'}</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>{translateMaterial(ai.material, en)}</Text>
          </View>
        )}
        {ai.pattern && ai.pattern !== 'uni' && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>{en ? 'Pattern' : 'Motif'}</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>{translateField('pattern', ai.pattern, en ? 'en' : 'fr')}</Text>
          </View>
        )}
        {ai.fit && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>{en ? 'Cut' : 'Coupe'}</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>{translateField('fit', ai.fit, en ? 'en' : 'fr')}</Text>
          </View>
        )}
        {ai.silhouette && ai.silhouette !== ai.fit && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>Silhouette</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>{translateField('silhouette', ai.silhouette, en ? 'en' : 'fr')}</Text>
          </View>
        )}
        {ai.length && ai.length !== 'standard' && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>{en ? 'Length' : 'Longueur'}</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>{translateField('length', ai.length, en ? 'en' : 'fr')}</Text>
          </View>
        )}
        {ai.neckline && ai.neckline !== 'null' && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>{en ? 'Neckline' : 'Encolure'}</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>{translateField('neckline', ai.neckline, en ? 'en' : 'fr')}</Text>
          </View>
        )}
        {ai.sleeveLength && ai.sleeveLength !== 'null' && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>{en ? 'Sleeves' : 'Manches'}</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>{translateField('sleeveLength', ai.sleeveLength, en ? 'en' : 'fr')}</Text>
          </View>
        )}
        {ai.priceRange && prices[ai.priceRange] && (
          <View style={{ minWidth: '47%', paddingVertical: 4 }}>
            <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase' }}>{en ? 'Price range' : 'Gamme'}</Text>
            <Text style={{ fontSize: 12, color: '#222', fontWeight: '500', marginTop: 2 }}>
              {prices[ai.priceRange].icon} {prices[ai.priceRange].label}
            </Text>
          </View>
        )}
      </View>

      {/* Niveau de formalité avec barre */}
      {fmt && (
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            {en ? 'Formality' : 'Formalité'}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 16 }}>{fmt.icon}</Text>
            <Text style={{ fontSize: 13, color: fmt.color, fontWeight: '600' }}>{fmt.label}</Text>
            <Text style={{ fontSize: 11, color: '#999' }}>· {ai.formality}/10</Text>
          </View>
          <View style={{ marginTop: 6, height: 4, backgroundColor: '#EEE', borderRadius: 2 }}>
            <View style={{
              width: `${(ai.formality / 10) * 100}%`,
              height: 4, borderRadius: 2,
              backgroundColor: fmt.color,
            }} />
          </View>
        </View>
      )}

      {/* Esprit du look — top 4 styles avec barres */}
      {topStyles.length > 0 && (
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
            {en ? 'Detected styles' : 'Esprit du look'}
          </Text>
          {topStyles.map(([style, score]) => (
            <View key={style} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text style={{ fontSize: 11, color: '#444', width: 90, textTransform: 'capitalize' }}>{translateStyleTag(style, en)}</Text>
              <View style={{ flex: 1, height: 4, backgroundColor: '#EEE', borderRadius: 2 }}>
                <View style={{
                  width: `${score}%`, height: 4,
                  backgroundColor: '#D4AF37', borderRadius: 2,
                }} />
              </View>
              <Text style={{ fontSize: 10, color: '#999', width: 30, textAlign: 'right' }}>{score}%</Text>
            </View>
          ))}
        </View>
      )}

      {/* Occasions adaptées */}
      {topOccasions.length > 0 && (
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
            {en ? 'Occasions' : 'Idéal pour'}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
            {topOccasions.map(([occ, score]) => (
              <View key={occ} style={{
                paddingHorizontal: 9, paddingVertical: 4,
                borderRadius: 12,
                backgroundColor: 'rgba(212, 175, 55, 0.12)',
                borderWidth: 1, borderColor: 'rgba(212, 175, 55, 0.4)',
              }}>
                <Text style={{ fontSize: 10, color: '#8B6F2E', fontWeight: '600', textTransform: 'capitalize' }}>
                  {translateOccasion(occ, en)} · {score}%
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Saisonnalité + température + moment de la journée */}
      {(Array.isArray(ai.seasonality) && ai.seasonality.length > 0) || ai.timeOfDay ? (
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
            {en ? 'Seasons' : 'Quand le porter'}
          </Text>
          {Array.isArray(ai.seasonality) && ai.seasonality.length > 0 && (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginBottom: 4 }}>
              {ai.seasonality.map(s => (
                <View key={s} style={{
                  paddingHorizontal: 8, paddingVertical: 3,
                  backgroundColor: '#FFF', borderWidth: 1, borderColor: '#DDD', borderRadius: 12,
                }}>
                  <Text style={{ fontSize: 10, color: '#555', fontWeight: '500' }}>
                    {seasons[s.toLowerCase()] || s}
                  </Text>
                </View>
              ))}
            </View>
          )}
          {Array.isArray(ai.temperatureRange) && ai.temperatureRange.length === 2
            && (ai.temperatureRange[0] !== 0 || ai.temperatureRange[1] !== 0) && (() => {
            const [min, max] = ai.temperatureRange;
            // v1.12.25 : si currentTempC fournie, on indique si l'item est adapté
            let warningInfo = null;
            if (typeof currentTempC === 'number' && !isNaN(currentTempC)) {
              if (currentTempC < min - 8 || currentTempC > max + 8) {
                warningInfo = {
                  emoji: '⚠️',
                  color: '#C62828',
                  text: currentTempC > max
                    ? (en ? `Too hot today (${Math.round(currentTempC)}°C)`  : `Trop chaud aujourd'hui (${Math.round(currentTempC)}°C)`)
                    : (en ? `Too cool today (${Math.round(currentTempC)}°C)` : `Trop frais aujourd'hui (${Math.round(currentTempC)}°C)`),
                };
              } else if (currentTempC < min - 3 || currentTempC > max + 3) {
                warningInfo = {
                  emoji: '⚠️',
                  color: '#EF6C00',
                  text: currentTempC > max
                    ? (en ? `A bit hot today (${Math.round(currentTempC)}°C)`  : `Un peu chaud aujourd'hui (${Math.round(currentTempC)}°C)`)
                    : (en ? `A bit cool today (${Math.round(currentTempC)}°C)` : `Un peu frais aujourd'hui (${Math.round(currentTempC)}°C)`),
                };
              } else if (currentTempC < min || currentTempC > max) {
                warningInfo = {
                  emoji: '✓',
                  color: '#7CB342',
                  text: en
                    ? `OK today (${Math.round(currentTempC)}°C, edge)`
                    : `OK aujourd'hui (${Math.round(currentTempC)}°C, limite)`,
                };
              } else {
                warningInfo = {
                  emoji: '✓',
                  color: '#388E3C',
                  text: en
                    ? `Ideal today (${Math.round(currentTempC)}°C)`
                    : `Idéal aujourd'hui (${Math.round(currentTempC)}°C)`,
                };
              }
            }
            return (
              <View style={{ marginTop: 2 }}>
                <Text style={{ fontSize: 11, color: '#666' }}>
                  🌡️ {en ? `Ideal between ${min}°C and ${max}°C` : `Idéal entre ${min}°C et ${max}°C`}
                </Text>
                {warningInfo && (
                  <Text style={{ fontSize: 11, color: warningInfo.color, fontWeight: '600', marginTop: 2 }}>
                    {warningInfo.emoji} {warningInfo.text}
                  </Text>
                )}
              </View>
            );
          })()}
          {ai.timeOfDay && timesOfDay[ai.timeOfDay] && (
            <Text style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
              {timesOfDay[ai.timeOfDay]}
            </Text>
          )}
        </View>
      ) : null}

      {/* Polyvalence */}
      {typeof ai.versatility === 'number' && ai.versatility > 0 && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4, marginBottom: ai.trendReason ? 8 : 0 }}>
          <Text style={{ fontSize: 11 }}>♻️</Text>
          <Text style={{ fontSize: 11, color: '#444', flex: 1 }}>
            {en ? 'Versatility' : 'Polyvalence'} : {ai.versatility}/10
            <Text style={{ color: '#888' }}> — {versatilityComment(ai.versatility, en)}</Text>
          </Text>
        </View>
      )}

      {/* Trend reason / tag (si trending) */}
      {(ai.trendReason || ai.trendTag) && (ai.isTrendy || (ai.trendScore || 0) >= 70) && (
        <View style={{
          marginTop: 8, paddingTop: 8,
          borderTopWidth: 1, borderTopColor: 'rgba(255, 71, 87, 0.15)',
        }}>
          {ai.trendTag && (
            <Text style={{ fontSize: 11, color: '#FF4757', fontWeight: '700', marginBottom: 2 }}>
              {ai.trendTag}
            </Text>
          )}
          {ai.trendReason && (
            <Text style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
              {ai.trendReason}
            </Text>
          )}
        </View>
      )}

      {/* Notes IA (champ libre du dressing) */}
      {ai.notes && (
        <View style={{ marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#EAE0C6' }}>
          <Text style={{ fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>
            {en ? 'AI tip' : 'Conseil IA'}
          </Text>
          <Text style={{ fontSize: 12, color: '#444', fontStyle: 'italic', lineHeight: 17 }}>
            💬 {ai.notes}
          </Text>
        </View>
      )}
    </View>
  );
}
